import { UpdatePilotRotationSettingsRequest, tryUpdateAppSettingsV2 } from '@/actions/appSettings';
import { AppSettingsV2Updatable } from '@/actions/updatable';
import { integrationSelectOptions } from '@/helpers/integrationSelectHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectIntegrationsForCapability,
    selectIsPilotRotationSyncEnabled,
    selectPilotRotationSettings,
} from '@/selectors/selectors';
import { AppComponentProps } from '@/typescriptHelpers';
import { globalMessage } from '@/utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { IntegrationCapability, PilotRotationSettings } from '../../types';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';

interface PilotRotationSettingsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
    workflowId: string;
}

const PilotRotationSettingsModal: React.FC<PilotRotationSettingsModalProps> = (props) => {
    const [form] = Form.useForm<PilotRotationSettings>();
    const dispatch = useDispatch();

    const pilotRotationSettings = useSelector(selectPilotRotationSettings(props.appId));
    const schedulingIntegrations = useSelector((state: RootState) =>
        selectIntegrationsForCapability(state, props.appId, IntegrationCapability.Scheduling),
    );
    const isSyncEnabled = useSelector(selectIsPilotRotationSyncEnabled(props.appId));

    const initialValues: Partial<PilotRotationSettings> = {
        syncFromExternalSchedule: isSyncEnabled,
        schedulingInstallationId:
            (pilotRotationSettings?.schedulingInstallationId ?? schedulingIntegrations[0]?.installationId) || undefined,
    };

    const syncFromExternalSchedule = Form.useWatch('syncFromExternalSchedule', form);
    const noSchedulingIntegrations = schedulingIntegrations.length === 0;

    const isLoading = useIsLoading(AppSettingsV2Updatable.UpdatePilotRotationSettings);
    const success = useUpdated(AppSettingsV2Updatable.UpdatePilotRotationSettings);
    const error = useError(AppSettingsV2Updatable.UpdatePilotRotationSettings);

    React.useEffect(() => {
        if (success) {
            globalMessage.success('Release pilot rotation settings updated.');
            props.onCancel();
        }
    }, [success]);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    const savePilotRotationSettings = (values: PilotRotationSettings) => {
        const request: UpdatePilotRotationSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            workflowId: props.workflowId,
            syncFromExternalSchedule: values.syncFromExternalSchedule,
            schedulingInstallationId: values.syncFromExternalSchedule ? values.schedulingInstallationId : '',
        };
        dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.UpdatePilotRotationSettings, request));
    };

    return (
        <Modal
            title="Release pilot rotation settings"
            open={props.open}
            onCancel={props.onCancel}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={props.onCancel}>
                    Cancel
                </Button>,
                <PermissionedActionButton
                    key="save"
                    type="primary"
                    actionType={RunwayUserAction.UAUpdateAppSettings}
                    loading={isLoading}
                    onClick={() => form.submit()}
                >
                    Save
                </PermissionedActionButton>,
            ]}
        >
            <Form
                form={form}
                onFinish={savePilotRotationSettings}
                layout="vertical"
                preserve={false}
                initialValues={initialValues}
            >
                <Tooltip
                    title="Connect a scheduling integration to sync the rotation from an on-call schedule"
                    open={noSchedulingIntegrations ? undefined : false}
                >
                    <Form.Item name="syncFromExternalSchedule" valuePropName="checked" style={{ marginBottom: 8 }}>
                        <Checkbox disabled={noSchedulingIntegrations && !syncFromExternalSchedule}>
                            Sync from external schedule
                        </Checkbox>
                    </Form.Item>
                </Tooltip>
                <Form.Item
                    label=""
                    name="schedulingInstallationId"
                    rules={[{ required: !!syncFromExternalSchedule, message: 'Please select an integration' }]}
                    style={{ paddingLeft: 24 }}
                >
                    <Select
                        disabled={!syncFromExternalSchedule}
                        style={{ opacity: syncFromExternalSchedule ? 1 : 0.5 }}
                        options={integrationSelectOptions(schedulingIntegrations)}
                        placeholder="Select an integration"
                        optionFilterProp="title"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PilotRotationSettingsModal;
